// Ruleset sentinels shared by RulesetEditor, RulesetViewer and Rules. These strings are
// stored and returned by the infection-case-api, so they are part of that contract.

// Node kinds in a ruleset body. Criterion/CriteriaGroup form a tree; OutbreakThreshold is
// a single root node with no children. RulesetViewer renders only the tree kinds: the
// per-case evaluation it shows covers SyndromicSurveillance rulesets only, so an
// OutbreakThreshold never reaches it.
export const NodeType = Object.freeze({
  CriteriaGroup: "CriteriaGroup",
  Criterion: "Criterion",
  OutbreakThreshold: "OutbreakThreshold",
});

// What a Criterion matches on, saved as the node's `criteriaType`.
export const CriteriaType = Object.freeze({
  Symptom: "Symptom",
  Organism: "Organism",
});

// Saved as the ruleset's `useCase`. Only OutbreakDetection uses the threshold body.
export const UseCase = Object.freeze({
  SyndromicSurveillance: "SyndromicSurveillance",
  UTIProcessSurveillance: "UTIProcessSurveillance",
  OutbreakDetection: "OutbreakDetection",
});

// Display labels for the use-case select, in menu order.
export const USE_CASE_OPTIONS = Object.freeze([
  { value: UseCase.SyndromicSurveillance, label: "Syndromic Surveillance" },
  { value: UseCase.UTIProcessSurveillance, label: "UTI Process Surveillance" },
  { value: UseCase.OutbreakDetection, label: "Outbreak Detection" },
]);

// OutbreakThreshold.scope — which cases count toward the threshold. Same strings as
// the outbreak-management-api's Outbreak.scope.
export const OutbreakScope = Object.freeze({
  Unit: "Unit",
  Facility: "Facility",
});

// Starting values for a new threshold; mirror the API's seeded defaults.
export const OUTBREAK_THRESHOLD_DEFAULTS = Object.freeze({
  minCases: 2,
  windowHours: 48,
  scope: OutbreakScope.Unit,
  minSharedSymptoms: 1,
});

// Tree-shaped bodies (Criterion / CriteriaGroup) and the threshold body are not
// convertible; callers use this to decide when a use-case change must clear the body.
export const isThresholdUseCase = (useCase) => useCase === UseCase.OutbreakDetection;

// The API requires whole numbers for every threshold count. element-ui 1.x's input-number
// has no precision prop and keeps a typed decimal, so the save gate checks instead.
export const THRESHOLD_INTEGER_FIELDS = Object.freeze(["minCases", "windowHours", "minSharedSymptoms"]);

export const hasIntegerThresholds = (threshold) =>
  !!threshold && THRESHOLD_INTEGER_FIELDS.every((field) => Number.isInteger(threshold[field]));
